import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  
  // 2FA States
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [show2FAVerify, setShow2FAVerify] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [twoFASecret, setTwoFASecret] = useState('');
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);

  // FIXED: Always use port 5000 for backend in development
  const getApiBaseUrl = () => {
    return 'https://tailorapi.mentoran.in';
  };

  // Check 2FA status on component mount
  useEffect(() => {
    check2FAStatus();
  }, []);

  const check2FAStatus = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      console.log('🔐 Checking 2FA status at:', `${baseUrl}/api/admin/check-2fa-status`);
      
      const response = await axios.get(`${baseUrl}/api/admin/check-2fa-status`, {
        timeout: 5000
      });
      
      console.log('✅ 2FA Status Response:', response.data);
      setIs2FAEnabled(response.data.is_2fa_enabled);
    } catch (error) {
      console.error('❌ Error checking 2FA status:', error);
      setError('Cannot connect to authentication server. Please ensure backend is running on port 5000.');
      setIs2FAEnabled(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (username === 'admin' && password === 'admin12345#') {
        if (is2FAEnabled) {
          setShow2FAVerify(true);
        } else {
          localStorage.setItem('isAuthenticated', 'true');
          navigate('/admin/dashboard');
        }
      } else {
        setError('Invalid username or password');
      }
    } catch (error) {
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASetup = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const baseUrl = getApiBaseUrl();
      console.log('🚀 Attempting to enable 2FA at:', `${baseUrl}/api/admin/enable-2fa`);
      
      const response = await axios.post(`${baseUrl}/api/admin/enable-2fa`, 
        { admin_id: 'admin' },
        { 
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('✅ 2FA Setup Response:', response.data);
      
      if (response.data.success) {
        setQrCode(response.data.qr_code);
        setTwoFASecret(response.data.secret);
        setShow2FASetup(true);
        setIs2FAEnabled(true);
        setError('');
      } else {
        setError(response.data.error || 'Failed to setup 2FA');
      }
    } catch (error) {
      console.error('❌ 2FA Setup Error Details:', error);
      
      if (error.code === 'ECONNREFUSED') {
        setError('Backend server is not running. Please start the Flask server on port 5000.');
      } else if (error.response) {
        const serverError = error.response.data;
        console.error('Server error details:', serverError);
        setError(`Server error (${error.response.status}): ${serverError.error || serverError.message || 'Unknown error'}`);
      } else if (error.request) {
        setError('No response from server. Please check if the backend is running on port 5000.');
      } else {
        setError(`Failed to setup 2FA: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FAVerification = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      setError('Please enter a valid 6-digit 2FA code');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      const baseUrl = getApiBaseUrl();
      const response = await axios.post(`${baseUrl}/api/admin/verify-2fa`, {
        code: twoFACode,
        admin_id: 'admin'
      });

      console.log('✅ 2FA Verification Response:', response.data);

      if (response.data.authenticated) {
        localStorage.setItem('isAuthenticated', 'true');
        navigate('/admin/dashboard');
      } else {
        setError('Invalid 2FA code. Please try again.');
      }
    } catch (error) {
      console.error('❌ 2FA Verification Error:', error);
      if (error.response) {
        setError(error.response.data.error || '2FA verification failed');
      } else {
        setError('2FA verification failed. Please check your connection.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const baseUrl = getApiBaseUrl();
      const response = await axios.post(`${baseUrl}/api/admin/disable-2fa`, {
        admin_id: 'admin'
      });
      
      if (response.data.success) {
        setIs2FAEnabled(false);
        setShow2FASetup(false);
        setError('');
        alert('2FA has been disabled successfully.');
      }
    } catch (error) {
      console.error('❌ Disable 2FA Error:', error);
      setError('Failed to disable 2FA. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip2FASetup = () => {
    setShow2FASetup(false);
    localStorage.setItem('isAuthenticated', 'true');
    navigate('/admin/dashboard');
  };

  // Test backend connection
  const testBackendConnection = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await axios.get(`${baseUrl}/api/health`, { timeout: 5000 });
      alert(`✅ Backend is running!\nStatus: ${response.data.status}\nDatabase: ${response.data.database}`);
      
      // Also test 2FA endpoint
      const twoFAResponse = await axios.get(`${baseUrl}/api/admin/check-2fa-status`, { timeout: 5000 });
      alert(`2FA Status: ${twoFAResponse.data.is_2fa_enabled ? 'ENABLED' : 'DISABLED'}`);
    } catch (error) {
      alert(`❌ Backend connection failed: ${error.message}`);
    }
  };

  // Test 2FA enable directly
  const test2FAEnable = async () => {
    try {
      const baseUrl = getApiBaseUrl();
      const response = await axios.post(`${baseUrl}/api/admin/enable-2fa`, { admin_id: 'admin' });
      console.log('Direct 2FA test:', response.data);
      if (response.data.success) {
        alert('✅ 2FA enable test successful!');
        setQrCode(response.data.qr_code);
        setTwoFASecret(response.data.secret);
        setShow2FASetup(true);
      } else {
        alert(`❌ 2FA enable failed: ${response.data.error}`);
      }
    } catch (error) {
      alert(`❌ 2FA enable test failed: ${error.message}`);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row justify-content-center align-items-center min-vh-100" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
        <div className="col-md-4">
          <div className="card shadow">
            <div className="card-body p-5">
              
              {/* Connection Info */}
              <div className="text-center mb-3">
                <small className="text-muted">
                  <i className="fas fa-info-circle me-1"></i>
                  Backend: localhost:5000 | Frontend: localhost:3000
                </small>
              </div>

              {/* Connection Test Buttons */}
              <div className="text-end mb-2">
                <button 
                  className="btn btn-sm btn-outline-info me-2"
                  onClick={testBackendConnection}
                  title="Test backend connection"
                >
                  <i className="fas fa-plug"></i> Test Connection
                </button>
                <button 
                  className="btn btn-sm btn-outline-warning"
                  onClick={test2FAEnable}
                  title="Test 2FA enable directly"
                >
                  <i className="fas fa-bolt"></i> Test 2FA
                </button>
              </div>

              {/* 2FA Setup Modal */}
              {show2FASetup && (
                <div className="text-center">
                  <h3 className="text-center mb-4" style={{color: '#300885'}}>
                    <i className="fas fa-qrcode me-2"></i>
                    Setup 2FA
                  </h3>
                  
                  <div className="alert alert-info">
                    <strong>Step 1:</strong> Scan this QR code with <strong>Microsoft Authenticator</strong>
                  </div>
                  
                  {qrCode && (
                    <div className="mb-4">
                      <img src={qrCode} alt="QR Code" className="img-fluid border rounded" style={{maxWidth: '200px'}} />
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <p className="text-muted small">
                      <strong>Manual Entry:</strong> 
                      <br />
                      <code className="bg-light p-2 rounded d-block mt-1">{twoFASecret}</code>
                    </p>
                  </div>
                  
                  <div className="alert alert-warning">
                    <strong>Step 2:</strong> Enter the 6-digit code from your app
                  </div>
                  
                  <div className="mb-3">
                    <input
                      type="text"
                      className="form-control text-center"
                      placeholder="000000"
                      value={twoFACode}
                      onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength="6"
                      style={{fontSize: '1.5rem', letterSpacing: '5px'}}
                    />
                  </div>
                  
                  <button 
                    className="btn w-100 mb-2"
                    onClick={handle2FAVerification}
                    disabled={isLoading || twoFACode.length !== 6}
                    style={{backgroundColor: '#300885', color: 'white'}}
                  >
                    {isLoading ? 'Verifying...' : 'Verify & Enable 2FA'}
                  </button>
                  
                  <button 
                    className="btn btn-outline-secondary w-100"
                    onClick={handleSkip2FASetup}
                  >
                    Skip 2FA Setup
                  </button>
                </div>
              )}

              {/* 2FA Verification */}
              {show2FAVerify && (
                <div className="text-center">
                  <h3 className="text-center mb-4" style={{color: '#300885'}}>
                    <i className="fas fa-lock me-2"></i>
                    Two-Factor Authentication
                  </h3>
                  
                  <div className="alert alert-info mb-4">
                    Please enter the 6-digit code from your authenticator app
                  </div>
                  
                  {error && (
                    <div className="alert alert-danger" role="alert">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-4">
                    <input
                      type="text"
                      className="form-control text-center"
                      placeholder="Enter 6-digit code"
                      value={twoFACode}
                      onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      maxLength="6"
                      style={{fontSize: '1.5rem', letterSpacing: '5px'}}
                    />
                  </div>
                  
                  <button 
                    className="btn w-100 mb-3"
                    onClick={handle2FAVerification}
                    disabled={isLoading || twoFACode.length !== 6}
                    style={{backgroundColor: '#300885', color: 'white'}}
                  >
                    {isLoading ? 'Verifying...' : 'Verify & Login'}
                  </button>
                  
                  <button 
                    className="btn btn-outline-secondary w-100"
                    onClick={() => {
                      setShow2FAVerify(false);
                      setTwoFACode('');
                      setError('');
                    }}
                  >
                    Back to Login
                  </button>
                </div>
              )}

              {/* Regular Login Form */}
              {!show2FASetup && !show2FAVerify && (
                <>
                  <h2 className="text-center mb-4" style={{color: '#300885'}}>
                    <i className="fas fa-user-shield me-2"></i>
                    Admin Login
                  </h2>
                  
                  <form onSubmit={handleLogin}>
                    {error && (
                      <div className="alert alert-danger" role="alert">
                        {error}
                      </div>
                    )}
                    
                    <div className="mb-3">
                      <label className="form-label">Username</label>
                      <input
                        type="text"
                        className="form-control"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                    
                    <button 
                      type="submit" 
                      className="btn w-100 mb-3"
                      disabled={isLoading}
                      style={{backgroundColor: '#300885', color: 'white'}}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Logging in...
                        </>
                      ) : (
                        'Login'
                      )}
                    </button>
                  </form>

                  {/* 2FA Management Section */}
                  <div className="border-top pt-3 mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted">
                        <i className="fas fa-shield-alt me-2"></i>
                        Two-Factor Authentication: 
                      </span>
                      <span className={is2FAEnabled ? "text-success fw-bold" : "text-warning fw-bold"}>
                        {is2FAEnabled ? "ENABLED" : "DISABLED"}
                      </span>
                    </div>
                    
                    <div className="d-grid gap-2">
                      {!is2FAEnabled ? (
                        <button 
                          className="btn btn-outline-primary btn-sm"
                          onClick={handle2FASetup}
                          disabled={isLoading}
                        >
                          <i className="fas fa-qrcode me-1"></i>
                          Enable 2FA
                        </button>
                      ) : (
                        <button 
                          className="btn btn-outline-danger btn-sm"
                          onClick={handleDisable2FA}
                          disabled={isLoading}
                        >
                          <i className="fas fa-shield-slash me-1"></i>
                          Disable 2FA
                        </button>
                      )}
                    </div>
                    
                    {!is2FAEnabled && (
                      <div className="mt-2">
                        <small className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          Enable 2FA for enhanced security.
                        </small>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            
            <div className="card-footer text-center text-muted">
              <small>Pre-production Environment • Tailoring Management System</small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;